<template>
  <div class="container-fluid">
    <page-header
      class="page-header__extended"
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-md-8 page-header__extended--left-side"
      right-side-class="
        col-md-4 d-flex align-items-end justify-content-end mb-2
      "
    />
    <validation-observer ref="guideForm">
      <general-info
        v-model="model"
        :options="options"
        :statistics="statistics"
        @app-changed="getModules"
        @module-changed="getLinks"
      />
      <validation-provider v-slot="{ errors }" rules="requiredLength:1">
        <links-handler v-model="model.screens" :error="errors[0]" />
      </validation-provider>
      <validation-provider v-slot="{ errors }" rules="requiredGuide">
        <guide-and-video-info v-model="model" :error="errors[0]" />
      </validation-provider>
    </validation-observer>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import GeneralInfo from '@/views/operations/guidesAndVideos/components/GeneralInfo.vue';
import LinksHandler from '@/views/operations/guidesAndVideos/components/LinksHandler.vue';
import GuideAndVideoInfo from '@/views/operations/guidesAndVideos/components/GuideAndVideoInfo.vue';
import {
  getModules as _getModules,
  getLinks as _getLinks,
  getApplications as _getApplications,
} from '@/modules/common/code.api.js';
import { saveAsync, getGuideVideo } from '@/modules/guide-video/api';
import objectToFormData from 'object-to-formdata';
import alert from '@/mixin/alert.mixin';
import { extend } from 'vee-validate';

extend('requiredLength', {
  validate(value, args) {
    return {
      required: true,
      valid: value?.length >= args.length,
    };
  },
  computesRequired: true,
  params: ['length'],
  message: 'El enlace de la pantalla es requerido.',
});

extend('requiredGuide', {
  validate(value) {
    return {
      required: true,
      valid: value?.file || value?.videoUrl?.length,
    };
  },
  computesRequired: true,
  message: 'Favor añadir al menos una guía y/o video.',
});

export default {
  name: 'ManageGuidesAndVideos',
  mixins: [alert],
  components: { PageHeader, GeneralInfo, LinksHandler, GuideAndVideoInfo },
  data: () => ({
    model: {
      screens: [],
      guideDocument: {},
    },
    options: {
      applications: [],
      modules: [],
      links: [],
    },
    statistics: {
      downloads: {
        title: 'Descargas de guía',
        icon: 'fa fa-book',
        quantity: 0,
        lastWeek: 25000,
      },
      seenVideos: {
        title: 'Video visto',
        icon: 'fa fa-video',
        quantity: 0,
        lastWeek: 25000,
      },
    },
    page: {
      icon: 'fas fa-calculator',
      title: 'Configuración de Guías o Videos',
    },
  }),
  computed: {
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          click: () => this.onSaveHandler(),
        },
      ];
    },
  },
  async created() {
    await Promise.all([this.getApps(), this.getGuideData()]);
  },
  methods: {
    async getGuideData() {
      const { id } = this.$route.params;
      if (!id) return;

      const { data } = await getGuideVideo(id);
      this.model = { ...data };

      await Promise.all([
        this.getModules(this.model.application, false),
        this.getLinks(this.model.module, false),
      ]);
    },
    async getApps() {
      const { data } = await _getApplications();
      this.options.applications = data.map(({ keyId, name }) => ({
        id: keyId,
        name,
      }));
    },
    async getModules(application, reset = true) {
      if (reset) {
        this.model = {
          ...this.model,
          link: null,
          module: null,
        };
        this.options = {
          ...this.options,
          modules: [],
          links: [],
        };
        await this.$refs.guideForm.reset();
      }
      if (!application) return;

      const { data } = await _getModules(application.id);
      this.options.modules = [...data];
    },
    async getLinks(module, reset = true) {
      if (reset) {
        this.model.link = null;
        this.options.links = [];
        await this.$refs.guideForm.reset();
      }
      if (!module) return;

      const { data } = await _getLinks(module.id);
      this.options.links = [...data];
    },
    async onSaveHandler() {
      const valid = await this.$refs.guideForm.validate();
      if (!valid) {
        this.ShowToast(
          'Error.',
          'Favor de completar la información solicitada.',
          'error'
        );
        return;
      }

      let payload = objectToFormData(
        {
          ...this.model,
          moduleId: this.model.module.id,
          applicationId: this.model.application.id,
          linkId: this.model.link.id,
        },
        { indices: true }
      );

      await saveAsync(payload)
        .then(() => {
          this.$router.go(-1);
          this.ShowToast(
            'Proceso Exitoso.',
            'La información ha sido guardada exitosamente.',
            'success'
          );
        })
        .catch(() => {
          this.ShowToast(
            'Error.',
            'Ha ocurrido un error al guardar la información.',
            'error'
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .page-header__extended {
    &--left-side {
      .tag-badge {
        margin-bottom: 1rem;
      }
      .controls-wrap {
        display: none;
      }
    }
  }
}
</style>
